/**
 * Coupon discount opt-in on product cards.
 *
 * Native price HTML is left alone. Toggle is appended beside it.
 * Checkbox ON:  apply coupon € into existing sale/compare nodes.
 * Checkbox OFF: restore those nodes to their original HTML.
 */
(function () {
  'use strict';

  const SECTION_ID = 'pcp-discount-data';
  const DESKTOP_MQ = '(min-width: 700px)';
  const discountCache = new WeakMap();
  const initialStateCache = new WeakMap();

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (Number.isNaN(cents)) return '';

    const settings = window.themeVariables?.settings || {};
    const formatString =
      format ||
      (settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat) ||
      '{{amount}}';

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision ?? 2);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands ?? ','));
      const fraction = parts[1] ? (decimal ?? '.') + parts[1] : '';
      return whole + fraction;
    }

    const match = formatString.match(placeholderRegex);
    const token = match ? match[1] : 'amount';
    let value = '';

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function fromLabel(template, price) {
    return template ? template.replace('@@', price) : price;
  }

  function getBlockControls(block) {
    return {
      checkbox: block.querySelector('.pcp-reveal-checkbox'),
      button: block.querySelector('.pcp-reveal-btn'),
    };
  }

  function setControlsDisabled(block, disabled) {
    const { checkbox, button } = getBlockControls(block);
    if (checkbox) checkbox.style.cursor = disabled ? 'wait' : '';
    if (button) button.style.cursor = disabled ? 'wait' : '';
  }

  function setControlState(block, revealed) {
    const { checkbox, button } = getBlockControls(block);
    const bar = block.querySelector('.pcp-toggle-bar');

    if (checkbox) checkbox.checked = revealed;

    if (button) {
      button.setAttribute('aria-expanded', revealed ? 'true' : 'false');
      button.setAttribute('aria-checked', revealed ? 'true' : 'false');
      button.classList.toggle('pcp-reveal-btn--active', revealed);
    }

    if (bar) bar.classList.toggle('pcp-toggle-bar--active', revealed);
  }

  function isPriceListMode(block) {
    return (
      block.dataset.pcpMode === 'price-list' ||
      !!block.closest('price-list')
    );
  }

  function getPriceHost(block) {
    return (
      block.closest('price-list') ||
      block.closest('.cts-card__price') ||
      block.parentElement
    );
  }

  function getPriceNodes(block) {
    const host = getPriceHost(block);
    if (!host) return { sale: null, compare: null };

    if (isPriceListMode(block) || host.matches?.('price-list')) {
      return {
        sale: host.querySelector('sale-price'),
        compare: host.querySelector('compare-at-price'),
      };
    }

    return {
      sale: host.querySelector('.cts-price--current, .cts-price--regular'),
      compare: host.querySelector('.cts-price--original'),
    };
  }

  function ensureCompareNode(block, sale) {
    let { compare } = getPriceNodes(block);
    if (compare || !sale) return compare;

    const host = getPriceHost(block);
    if (!host) return null;

    if (isPriceListMode(block) || host.matches?.('price-list')) {
      compare = document.createElement('compare-at-price');
      compare.className =
        (block.dataset.pcpCompareClass || '').trim() || 'text-subdued line-through';
      compare.setAttribute('data-pcp-created', '1');
      sale.after(compare);
      return compare;
    }

    compare = document.createElement('span');
    compare.className = 'cts-price--original';
    compare.setAttribute('data-pcp-created', '1');
    sale.after(compare);
    return compare;
  }

  function cacheInitialState(block) {
    if (initialStateCache.has(block)) return;

    const { sale, compare } = getPriceNodes(block);
    initialStateCache.set(block, {
      saleClass: sale ? sale.className : '',
      saleHtml: sale ? sale.innerHTML : '',
      compareClass: compare ? compare.className : '',
      compareHtml: compare ? compare.innerHTML : '',
      compareHidden: compare ? compare.hasAttribute('hidden') : true,
      compareExisted: !!compare,
    });
  }

  function setLabeledPrice(el, label, formatted) {
    if (!el) return;
    el.innerHTML =
      (label ? '<span class="sr-only">' + label + '</span>' : '') + formatted;
  }

  async function fetchDiscountData(handle) {
    if (!handle) throw new Error('Missing product handle');

    const response = await fetch(
      '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID,
      {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      }
    );

    if (!response.ok) throw new Error('Discount fetch failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const payload = doc.querySelector('.pcp-discount-payload');

    if (!payload) throw new Error('No discount payload');

    const baseCents = parseInt(payload.dataset.pcpBase, 10) || 0;
    const saleCents = parseInt(payload.dataset.pcpSale, 10) || 0;

    if (saleCents <= 0 || baseCents <= saleCents) throw new Error('Invalid discount data');

    return {
      baseCents,
      saleCents,
      varies: payload.dataset.pcpVaries === '1',
    };
  }

  function showRevealed(block, control, discountData) {
    cacheInitialState(block);

    let { sale, compare } = getPriceNodes(block);
    if (!sale) return;

    const { baseCents, saleCents, varies } = discountData;
    const fromTemplate = control?.dataset.pcpF;
    const saleFormatted = varies
      ? fromLabel(fromTemplate, formatMoney(saleCents))
      : formatMoney(saleCents);
    const compareFormatted = varies
      ? fromLabel(fromTemplate, formatMoney(baseCents))
      : formatMoney(baseCents);
    const saleLabel = control?.dataset.pcpSaleLabel || 'Sale price';
    const regularLabel = control?.dataset.pcpRegularLabel || 'Regular price';
    const priceListMode = isPriceListMode(block) || !!block.closest('price-list');

    if (priceListMode) {
      const saleClass =
        (block.dataset.pcpSaleClass || '').trim() ||
        (block.closest('price-list.price-list--lg, .price-list--lg')
          ? 'text-lg text-on-sale'
          : 'text-on-sale');
      const compareClass =
        (block.dataset.pcpCompareClass || '').trim() || 'text-subdued line-through';

      sale.className = saleClass;
      setLabeledPrice(sale, saleLabel, saleFormatted);

      compare = ensureCompareNode(block, sale);
      if (compare) {
        compare.className = compareClass;
        compare.hidden = false;
        compare.removeAttribute('hidden');
        setLabeledPrice(compare, regularLabel, compareFormatted);
      }
    } else {
      sale.className = 'cts-price--current';
      sale.textContent = saleFormatted;

      compare = ensureCompareNode(block, sale);
      if (compare) {
        compare.className = 'cts-price--original';
        compare.hidden = false;
        compare.removeAttribute('hidden');
        compare.textContent = compareFormatted;
      }
    }

    block.classList.add('pcp-block--revealed');
    block.classList.remove('pcp-block--error');
    const host = getPriceHost(block);
    if (host) host.classList.add('pcp-price-host--revealed');
    setControlState(block, true);
  }

  function hideRevealed(block) {
    const cached = initialStateCache.get(block);
    const { sale, compare } = getPriceNodes(block);

    if (cached && sale) {
      sale.className = cached.saleClass;
      sale.innerHTML = cached.saleHtml;
    }

    if (compare) {
      if (compare.getAttribute('data-pcp-created') === '1' || (cached && !cached.compareExisted)) {
        compare.remove();
      } else if (cached) {
        compare.className = cached.compareClass;
        compare.innerHTML = cached.compareHtml;
        if (cached.compareHidden) {
          compare.hidden = true;
          compare.setAttribute('hidden', '');
        } else {
          compare.hidden = false;
          compare.removeAttribute('hidden');
        }
      }
    }

    block.classList.remove('pcp-block--revealed', 'pcp-block--loading', 'pcp-block--error');
    const host = getPriceHost(block);
    if (host) host.classList.remove('pcp-price-host--revealed');
    setControlState(block, false);
  }

  async function revealBlock(block, control) {
    if (block.classList.contains('pcp-block--loading')) return;

    cacheInitialState(block);
    block.classList.add('pcp-block--loading');
    setControlsDisabled(block, true);

    let discountData = discountCache.get(block);

    try {
      if (!discountData) {
        discountData = await fetchDiscountData(control.dataset.pcpHandle);
        discountCache.set(block, discountData);
      }
    } catch (error) {
      block.classList.add('pcp-block--error');
      block.classList.remove('pcp-block--loading');
      setControlsDisabled(block, false);
      setControlState(block, false);
      return;
    }

    showRevealed(block, control, discountData);
    block.classList.remove('pcp-block--loading');
    setControlsDisabled(block, false);
  }

  async function toggleBlock(block, button) {
    if (block.classList.contains('pcp-block--loading')) return;

    if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
      return;
    }

    await revealBlock(block, button);
  }

  function onClick(event) {
    const button = event.target.closest('.pcp-reveal-btn');
    if (!button || button.disabled) return;

    const block = button.closest('.pcp-block');
    if (!block) return;

    event.preventDefault();
    toggleBlock(block, button);
  }

  function onChange(event) {
    const checkbox = event.target;
    if (!checkbox.matches('.pcp-reveal-checkbox') || checkbox.disabled) return;

    const block = checkbox.closest('.pcp-block');
    if (!block) return;

    if (checkbox.checked) {
      if (!block.classList.contains('pcp-block--revealed')) {
        revealBlock(block, checkbox);
      }
    } else if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
    }
  }

  function syncControlsOnResize() {
    document.querySelectorAll('.pcp-block--revealed[data-pcp-id]').forEach((block) => {
      setControlState(block, true);
    });
  }

  function initBlocks(root) {
    (root || document).querySelectorAll('.pcp-block[data-pcp-id]').forEach((block) => {
      cacheInitialState(block);
      setControlState(block, false);
    });
  }

  function init() {
    initBlocks();
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);

    if (window.matchMedia) {
      const mq = window.matchMedia(DESKTOP_MQ);
      if (mq.addEventListener) {
        mq.addEventListener('change', syncControlsOnResize);
      } else if (mq.addListener) {
        mq.addListener(syncControlsOnResize);
      }
    }

    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node.parentElement || node);
          } else if (node.querySelector?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
